import { Link } from 'react-router-dom'

import { api, unwrap } from '../api/client'
import { useSealedQuery } from '../api/sealed'
import { useSessionEra } from '../documents/held'
import { t } from '../i18n'
import { ErrorNotice, Pending, formatDate } from '../ui'

/** How many milestones the card lists. The schedule screen has the rest. */
export const MILESTONES_SHOWN = 5

/** Pages of activities read to find them; the route answers at most 500 a page. */
const PAGES = 4

type Activity = {
  id: string
  name: string
  activity_type: string
  is_critical: boolean
  planned_finish: string | null
  early_finish: string | null
  actual_finish: string | null
}

/** What the card read, and whether it read everything that finishes from today. */
type Read = { activities: Activity[]; complete: boolean }

/** The date a milestone is expected: the schedule's forecast, then its plan. */
export function dueOf(activity: Activity): string | null {
  return activity.early_finish ?? activity.planned_finish
}

/** The next unfinished milestones, soonest first. */
export function nextMilestones(activities: Activity[], today: string): Activity[] {
  return activities
    .filter((activity) => activity.activity_type === 'milestone' && !activity.actual_finish)
    .filter((activity) => {
      const due = dueOf(activity)
      return due !== null && due >= today
    })
    .sort((a, b) => (dueOf(a) ?? '').localeCompare(dueOf(b) ?? ''))
    .slice(0, MILESTONES_SHOWN)
}

/**
 * The next few milestones of the current schedule.
 *
 * Read from the schedule's own activities: importers type a milestone as
 * "milestone", and "critical" is the schedule's own flag, so nothing here is
 * guessed. The route orders by activity code rather than date, so the card
 * reads what finishes from today on and sorts it here.
 */
export function MilestonesCard({ projectId }: { projectId: string }) {
  const today = new Date().toLocaleDateString('en-CA')
  // Read under this session and sealed on a refusal, so a refused read takes
  // the list away rather than leaving the last answer beside the refusal.
  const era = useSessionEra()
  const { query: read, data } = useSealedQuery({
    key: ['schedule', 'milestones', projectId, today],
    era,
    scope: `schedule/milestones/${projectId}`,
    retry: false,
    read: async (): Promise<Read> => {
      const all: Activity[] = []
      let cursor: string | undefined
      for (let page = 0; page < PAGES; page += 1) {
        const answer = unwrap(
          await api.GET('/schedule/activities', {
            params: {
              query: {
                project_id: projectId,
                finishes_after: today,
                limit: 500,
                ...(cursor ? { cursor } : {}),
              },
            },
          }),
        )
        all.push(...(answer.activities as Activity[]))
        if (!answer.next_cursor) return { activities: all, complete: true }
        cursor = answer.next_cursor
      }
      return { activities: all, complete: false }
    },
  })

  const rows = data ? nextMilestones(data.activities, today) : []

  return (
    <section className="card" aria-labelledby="milestones-heading">
      <div className="row">
        <h3 id="milestones-heading" style={{ flex: 1 }}>
          {t('home.milestones.title')}
        </h3>
        <Link to={`/p/${projectId}/schedule`}>{t('home.milestones.all')}</Link>
      </div>
      {read.isPending ? <Pending /> : null}
      {read.error ? <ErrorNotice error={read.error} retry={() => void read.refetch()} /> : null}
      {/* The route orders by activity code, not by date, and the card reads a
          bounded number of pages. Where it stopped short, an earlier milestone
          may be on a page it did not read, so it says so rather than calling
          these the next ones. */}
      {data && !data.complete ? (
        <p className="muted">{t('home.milestones.partial', { count: data.activities.length })}</p>
      ) : null}
      {data && data.complete && rows.length === 0 ? (
        <p className="muted">{t('home.milestones.empty')}</p>
      ) : null}
      <ul className="milestones">
        {rows.map((row) => (
          <li key={row.id}>
            <span className="when">{formatDate(dueOf(row))}</span>
            <span>{row.name}</span>
            {row.is_critical ? (
              <span className="chip" data-tone="bad">
                {t('home.milestones.critical')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
